import { useMemo } from 'react';

import { Link as MuiLink, Typography } from '@mui/material';
import { createLink, useNavigate, useSearch } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';

import AlgorithmCell from '../components/cells/AlgorithmCell';
import AlgorithmLevelCell from '../components/cells/AlgorithmLevelCell';
import AlgorithmStatusCell from '../components/cells/AlgorithmStatusCell';
import AlgorithmLevelFilter from '../components/filters/AlgorithmLevelFilter';
import AlgorithmsFilter from '../components/filters/AlgorithmsFilter';
import AlgorithmStatusFilter from '../components/filters/AlgorithmStatusFilter';
import DateFilter from '../components/filters/DateFilter';
import MinMaxFilter from '../components/filters/MinMaxFilter';
import { ALGORITHM_LEVEL } from '../schemas';
import { TraffickingInsertionOrderData } from '../types';
import createSelectColumn from './createSelectColumn';

import { formatCurrencyAsIs } from '@/shared/utils/utils';

const Link = createLink(MuiLink);

const columnHelper = createColumnHelper<TraffickingInsertionOrderData>();

const useInsertionOrderColumns = () => {
  const columns = useMemo(
    () => [
      createSelectColumn(columnHelper),
      columnHelper.accessor('insertionOrderName', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Insertion Order Name
          </Typography>
        ),
        cell: (props) => (
          <Link
            to='/trafficking/ad-unit'
            search={{ search: props.row.original.insertionOrderId }}
            underline='hover'
            color='primary.dark'
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {props.getValue()}
          </Link>
        ),
        size: 320,
      }),
      columnHelper.accessor('insertionOrderId', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Insertion Order ID
          </Typography>
        ),
        cell: (props) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {props.getValue()}
          </Typography>
        ),
        size: 180,
      }),
      columnHelper.accessor('campaignName', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Campaign Name
          </Typography>
        ),
        cell: (props) => (
          <Typography
            variant='body2'
            component='span'
            color='text.tertiary'
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {props.getValue()}
          </Typography>
        ),
        size: 280,
      }),
      columnHelper.accessor('campaignId', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Campaign ID
          </Typography>
        ),
        cell: (props) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {props.getValue()}
          </Typography>
        ),
        size: 160,
      }),
      columnHelper.accessor('budget', {
        enableSorting: false,
        header: () => (
          <Typography variant='body3' component='span'>
            Budget
          </Typography>
        ),
        cell: (props) => {
          const budget = props.getValue();

          if (budget === null) {
            return (
              <Typography
                variant='subtitleNumbers'
                component='span'
                color='text.secondary'
              >
                None
              </Typography>
            );
          }

          return (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {formatCurrencyAsIs(budget)}
            </Typography>
          );
        },
        size: 140,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <MinMaxFilter
                filterId='budgetFilter'
                buttonAriaLabel='budget filter'
                minPlaceholder='Min Budget'
                maxPlaceholder='Max Budget'
                searchParamMinValue={searchParams.budgetMin}
                searchParamMaxValue={searchParams.budgetMax}
                handleNavigation={(minValue, maxValue) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      budgetMin: minValue,
                      budgetMax: maxValue,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.accessor('startDate', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Start Date
          </Typography>
        ),
        cell: (props) =>
          props.getValue() ? (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {new Date(props.getValue()).toLocaleDateString()}
            </Typography>
          ) : (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.secondary'
            >
              None
            </Typography>
          ),
        size: 150,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <DateFilter
                filterId='startDateFilter'
                buttonAriaLabel='start date filter'
                searchParamStringDate={searchParams.startDate}
                handleNavigation={(newDate) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      startDate: newDate,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.accessor('endDate', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            End Date
          </Typography>
        ),
        cell: (props) =>
          props.getValue() ? (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {new Date(props.getValue()).toLocaleDateString()}
            </Typography>
          ) : (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.secondary'
            >
              None
            </Typography>
          ),
        size: 150,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <DateFilter
                filterId='endDateFilter'
                buttonAriaLabel='end date filter'
                searchParamStringDate={searchParams.endDate}
                handleNavigation={(newDate) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      endDate: newDate,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.accessor('algorithms', {
        enableSorting: true,
        sortDescFirst: false,
        header: () => (
          <Typography variant='body3' component='span'>
            Algorithms
          </Typography>
        ),
        cell: (props) => (
          <AlgorithmCell algorithmsStatus={props.row.original.algorithms} />
        ),
        size: 180,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <AlgorithmsFilter
                filterId='algorithmsFilter'
                buttonAriaLabel='algorithms filter'
                searchParamAlgorithmIds={searchParams.algorithmId}
                handleNavigation={(newAlgos) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      algorithmId: newAlgos,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.accessor('algorithmStatus', {
        enableSorting: true,
        sortDescFirst: false,
        header: () => (
          <Typography variant='body3' component='span'>
            Status
          </Typography>
        ),
        cell: (props) => (
          <AlgorithmStatusCell
            algorithmsStatus={props.row.original.algorithms}
          />
        ),
        size: 200,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <AlgorithmStatusFilter
                filterId='algorithmStatusFilter'
                buttonAriaLabel='algorithm status filter'
                searchParamAlgorithmStatuses={searchParams.status}
                handleNavigation={(selectedAlgoStatuses) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      status: selectedAlgoStatuses,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.display({
        id: 'algorithmLevel',
        enableSorting: false,
        header: () => (
          <Typography variant='body3' component='span'>
            Level
          </Typography>
        ),
        cell: (props) => (
          <AlgorithmLevelCell
            algorithmsStatus={props.row.original.algorithms}
          />
        ),
        size: 180,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <AlgorithmLevelFilter
                level={ALGORITHM_LEVEL.INSERTION_ORDER}
                filterId='algorithmLevelFilter'
                buttonAriaLabel='algorithm level filter'
                searchParamAlgorithmLevels={searchParams.level}
                handleNavigation={(selectedAlgoLevels) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      level: selectedAlgoLevels,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
    ],
    []
  );

  return columns;
};

export default useInsertionOrderColumns;
